import { useEffect, useMemo, useState } from "react";
import { Wind } from "lucide-react";
import { api } from "@/lib/api";

const BANDS = [
  { label: "BERBAHAYA", color: "#111111", text: "#ffffff" },
  { label: "SANGAT TIDAK SEHAT", color: "#e21b1b", text: "#ffffff" },
  { label: "TIDAK SEHAT", color: "#cdc410", text: "#1a1a05" },
  { label: "SEDANG", color: "#1f45d3", text: "#ffffff" },
  { label: "BAIK", color: "#0aa544", text: "#ffffff" },
];

const CATEGORY_STYLE = {
  Baik: { color: "#0aa544", text: "#ffffff" },
  Sedang: { color: "#1f45d3", text: "#ffffff" },
  "Tidak Sehat": { color: "#cdc410", text: "#1a1a05" },
  "Sangat Tidak Sehat": { color: "#e21b1b", text: "#ffffff" },
  Berbahaya: { color: "#111111", text: "#ffffff" },
};

const ADVISORY = {
  Baik: "Tingkat kualitas udara yang sangat baik, tidak memberikan efek negatif terhadap manusia, hewan, dan tumbuhan.",
  Sedang:
    "Tingkat kualitas udara yang masih dapat diterima pada kesehatan manusia, hewan, dan tumbuhan.",
  "Tidak Sehat":
    "Tingkat kualitas udara yang bersifat merugikan pada manusia, hewan, dan tumbuhan.",
  "Sangat Tidak Sehat":
    "Tingkat kualitas udara yang dapat meningkatkan resiko kesehatan pada sejumlah segmen populasi yang terpapar.",
  Berbahaya:
    "Tingkat kualitas udara yang dapat merugikan kesehatan secara serius pada populasi dan perlu penanganan cepat.",
};

const catOf = (v) =>
  v == null
    ? null
    : v <= 50
      ? "Baik"
      : v <= 100
        ? "Sedang"
        : v <= 200
          ? "Tidak Sehat"
          : v <= 300
            ? "Sangat Tidak Sehat"
            : "Berbahaya";

// Tinggi bar mengikuti pita kategori (masing-masing pita = 20% tinggi)
const barHeight = (v) => {
  if (v == null) return 0;
  const edges = [0, 50, 100, 200, 300, 500];
  let h = 0;
  for (let i = 0; i < 5; i++) {
    const lo = edges[i];
    const hi = edges[i + 1];
    if (v > hi) h += 20;
    else {
      h += ((v - lo) / (hi - lo)) * 20;
      break;
    }
  }
  return Math.max(3, Math.min(100, h));
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })} ${d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} WIB`;
};

const sub = (code) =>
  code === "PM10" ? (
    <>PM<sub>10</sub></>
  ) : code === "PM2.5" ? (
    <>PM<sub>2.5</sub></>
  ) : code === "SO2" ? (
    <>SO<sub>2</sub></>
  ) : code === "NO2" ? (
    <>NO<sub>2</sub></>
  ) : code === "O3" ? (
    <>O<sub>3</sub></>
  ) : (
    code
  );

export function IspuCard({ d, title }) {
  const cat = CATEGORY_STYLE[d.category] || CATEGORY_STYLE.Sedang;
  return (
    <div className="idp-card" data-testid={`ispu-card-${d.name.toLowerCase().replaceAll(" ", "-")}`}>
      {title && <div className="idp-card-title">{title}</div>}
      <div
        className="idp-banner"
        style={{ background: cat.color, color: cat.text }}
        data-testid="ispu-detail-banner"
      >
        <div className="idp-banner-val">
          <span>ISPU</span>
          <strong data-testid="ispu-detail-value">{d.ispu}</strong>
        </div>
        <div className="idp-banner-cat">
          <h4>{(d.category || "").toUpperCase()}</h4>
          <em>
            {sub(d.dominant)} &nbsp;|&nbsp; {fmtTime(d.time)}
          </em>
        </div>
      </div>

      <div className="idp-body">
        <div className="idp-legend">
          {BANDS.map((b) => (
            <div key={b.label} style={{ background: b.color, color: b.text }}>
              {b.label}
            </div>
          ))}
          <span className="idp-legend-cap">KATEGORI</span>
        </div>
        <div className="idp-bars">
          {d.pollutants.map((p) => {
            const c = CATEGORY_STYLE[catOf(p.ispu)] || {};
            return (
              <div
                className="idp-bar-col"
                key={p.code}
                data-testid={`ispu-bar-${p.code.toLowerCase().replace(".", "")}`}
              >
                <div
                  className="idp-track"
                  title={
                    p.ispu != null
                      ? `${p.code}: ISPU ${p.ispu} · konsentrasi ${p.conc} µg/m³ (rata-rata ${p.avg_hours} jam)`
                      : `${p.code}: data tidak tersedia`
                  }
                >
                  <i
                    style={{
                      height: `${barHeight(p.ispu)}%`,
                      background: c.color || "#334155",
                    }}
                  />
                  <b
                    className="idp-val"
                    style={{ background: c.color || "#334155", color: c.text || "#e2e8f0" }}
                  >
                    {p.ispu ?? "—"}
                  </b>
                </div>
                <span className="idp-bar-label">{sub(p.code)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="idp-advisory"
        style={{ background: cat.color, color: cat.text }}
        data-testid="ispu-detail-advisory"
      >
        {ADVISORY[d.category] || ""}
      </div>
      <div className="idp-source" data-testid="ispu-card-source">
        Sumber data: Open-Meteo Air Quality · CAMS (Copernicus) — konversi ISPU
        Permen LHK No. P.14/MENLHK/SETJEN/KUM.1/7/2020
      </div>
    </div>
  );
}

export default function IspuDetail({ compact = false, autoRotate = false, showAll = false }) {
  const [detail, setDetail] = useState({});
  const [active, setActive] = useState("");

  useEffect(() => {
    let timer;
    const load = () =>
      api
        .get("/ispu/detail")
        .then((r) => {
          const d = r.data.districts || {};
          setDetail(d);
          setActive((prev) => {
            if (prev && d[prev]) return prev;
            const worst = Object.values(d).sort((a, b) => b.ispu - a.ispu)[0];
            return worst ? worst.name : "";
          });
          // retry cepat 30 dtk bila kosong, normal 5 menit bila terisi
          timer = setTimeout(load, Object.keys(d).length ? 5 * 60000 : 30000);
        })
        .catch(() => {
          timer = setTimeout(load, 30000);
        });
    load();
    return () => clearTimeout(timer);
  }, []);

  const names = useMemo(() => Object.keys(detail), [detail]);

  useEffect(() => {
    if (!autoRotate || names.length < 2) return undefined;
    const t = setInterval(() => {
      setActive((prev) => {
        const i = names.indexOf(prev);
        return names[(i + 1) % names.length];
      });
    }, 5000);
    return () => clearInterval(t);
  }, [autoRotate, names]);

  const d = detail[active];
  if (!d) {
    if (compact) return null;
    return (
      <section className="ispu-detail-panel" data-testid="ispu-detail-panel">
        <div className="panel-heading">
          <div>
            <span className="section-kicker">GRAFIK ISPU PER KECAMATAN</span>
            <h3>Indeks Standar Pencemar Udara · per polutan</h3>
          </div>
          <Wind size={18} />
        </div>
        <p className="ispu-note" data-testid="ispu-detail-empty">
          Data ISPU sementara tidak tersedia dari penyedia (Open-Meteo/CAMS) —
          sistem mencoba lagi otomatis setiap 30 detik.
        </p>
      </section>
    );
  }

  if (showAll) {
    return (
      <section className="ispu-detail-panel" data-testid="ispu-detail-panel">
        {!compact && (
          <div className="panel-heading">
            <div>
              <span className="section-kicker">GRAFIK ISPU PER KECAMATAN</span>
              <h3>Indeks Standar Pencemar Udara · per polutan</h3>
            </div>
            <Wind size={18} />
          </div>
        )}
        <div className="idp-all" data-testid="ispu-all-grid">
          {names.map((n) => (
            <IspuCard key={n} d={detail[n]} title={`Kec. ${n}`} />
          ))}
        </div>
        {!compact && (
          <p className="ispu-note">
            Konversi ISPU sesuai Permen LHK P.14/2020 dari konsentrasi Open-Meteo/CAMS
            (PM &amp; SO2 &amp; NO2 rata-rata 24 jam · CO &amp; O3 rata-rata 8 jam).
            Parameter HC tidak tersedia dari sumber data.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="ispu-detail-panel" data-testid="ispu-detail-panel">
      {!compact && (
        <div className="panel-heading">
          <div>
            <span className="section-kicker">GRAFIK ISPU PER KECAMATAN</span>
            <h3>Indeks Standar Pencemar Udara · per polutan</h3>
          </div>
          <Wind size={18} />
        </div>
      )}

      <div className="idp-tabs" data-testid="ispu-detail-tabs">
        {names.map((n) => (
          <button
            key={n}
            className={n === active ? "on" : ""}
            onClick={() => setActive(n)}
            data-testid={`ispu-detail-tab-${n.toLowerCase().replaceAll(" ", "-")}`}
          >
            {n}
            <b style={{ background: (CATEGORY_STYLE[detail[n].category] || {}).color }}>
              {detail[n].ispu}
            </b>
          </button>
        ))}
      </div>

      <IspuCard d={d} />

      {!compact && (
        <p className="ispu-note">
          Konversi ISPU sesuai Permen LHK P.14/2020 dari konsentrasi Open-Meteo/CAMS
          (PM &amp; SO2 &amp; NO2 rata-rata 24 jam · CO &amp; O3 rata-rata 8 jam).
          Parameter HC tidak tersedia dari sumber data.
        </p>
      )}
    </section>
  );
}
